import React, { useState, useEffect } from 'react';
import { todayInTz, localMidnightUtc, addDays, startOfMonth } from './tzDates';

/**
 * Shared date-range picker. Six preset buttons + a Custom row that
 * reveals two date inputs. The selected preset is persisted to
 * localStorage so the user's choice survives reloads — same pattern
 * as the dashboard's view-mode and active-tab persistence.
 *
 * Timezone (added 2026-06-03)
 * ----------------------------
 * All "Today" / "Yesterday" / "Last N" / "This Month" math happens in the
 * account's IANA timezone (passed as the `timezone` prop). If omitted, we
 * fall back to the browser local timezone via
 * `Intl.DateTimeFormat().resolvedOptions().timeZone`. This means a user in
 * California viewing an Eastern-time account gets the account's "Yesterday"
 * (00:00 EST → 24:00 EST), not 00:00 PST → 24:00 PST. Matches how the
 * dashboard's "today" tally already works (via `org.timezone`).
 *
 * Why hand-rolled (no library)
 * ----------------------------
 * The presets cover ~95% of real use; the Custom row is two native
 * <input type="date"> which gives the platform date picker for free.
 * Pulling in react-datepicker etc. would add bundle size for an
 * interaction that's already fine.
 */

export type RangePreset = 'today' | 'yesterday' | 'last7' | 'last30' | 'thisMonth' | 'custom';

export interface DateRange {
  preset: RangePreset;
  from: Date;
  to: Date;
}

const PRESET_KEY = 'databodies.reporting.preset';

function browserTz(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function rangeForPreset(
  preset: Exclude<RangePreset, 'custom'>,
  tz: string
): { from: Date; to: Date } {
  const todayLocal = todayInTz(tz);
  const todayStartUtc = localMidnightUtc(todayLocal.year, todayLocal.month, todayLocal.day, tz);
  const now = new Date();
  switch (preset) {
    case 'today':
      return { from: todayStartUtc, to: now };
    case 'yesterday': {
      const yLocal = addDays(todayLocal, -1);
      const yStartUtc = localMidnightUtc(yLocal.year, yLocal.month, yLocal.day, tz);
      return { from: yStartUtc, to: todayStartUtc };
    }
    case 'last7': {
      const startLocal = addDays(todayLocal, -7);
      const startUtc = localMidnightUtc(startLocal.year, startLocal.month, startLocal.day, tz);
      return { from: startUtc, to: now };
    }
    case 'last30': {
      const startLocal = addDays(todayLocal, -30);
      const startUtc = localMidnightUtc(startLocal.year, startLocal.month, startLocal.day, tz);
      return { from: startUtc, to: now };
    }
    case 'thisMonth': {
      const monthStartLocal = startOfMonth(todayLocal);
      const startUtc = localMidnightUtc(monthStartLocal.year, monthStartLocal.month, monthStartLocal.day, tz);
      return { from: startUtc, to: now };
    }
  }
}

/**
 * Returns the default range the picker should start with. Reads the
 * preset from localStorage and resolves it in the given timezone. Used
 * once when the page mounts; subsequent updates flow through onChange.
 */
export function defaultRange(tz?: string): DateRange {
  const effectiveTz = tz || browserTz();
  const stored = localStorage.getItem(PRESET_KEY) as RangePreset | null;
  const preset: RangePreset = (
    stored === 'today' || stored === 'yesterday' || stored === 'last7' ||
    stored === 'last30' || stored === 'thisMonth' || stored === 'custom'
  ) ? stored : 'last7';
  if (preset === 'custom') {
    // Don't restore custom — start with last 7 instead. Stored custom
    // dates would be stale by the next session anyway.
    const { from, to } = rangeForPreset('last7', effectiveTz);
    return { preset: 'last7', from, to };
  }
  const { from, to } = rangeForPreset(preset, effectiveTz);
  return { preset, from, to };
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function parseLocalDateAtStart(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
}

function parseLocalDateAtEnd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 23, 59, 59, 999);
}

interface Props {
  value: DateRange;
  onChange: (next: DateRange) => void;
  onRefresh?: () => void;
  /**
   * IANA timezone for date math (e.g. 'America/New_York'). Falls back to
   * the browser's timezone if omitted. Should be set to the account's
   * configured timezone for multi-tenant correctness.
   */
  timezone?: string;
}

const PRESET_BUTTONS: Array<{ key: RangePreset; label: string }> = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7',     label: 'Last 7 Days' },
  { key: 'last30',    label: 'Last 30 Days' },
  { key: 'thisMonth', label: 'This Month' },
  { key: 'custom',    label: 'Custom' },
];

export default function DateRangePicker({ value, onChange, onRefresh, timezone }: Props) {
  const tz = timezone || browserTz();
  const [customFrom, setCustomFrom] = useState<string>(isoDate(value.from));
  const [customTo, setCustomTo] = useState<string>(isoDate(value.to));

  useEffect(() => {
    setCustomFrom(isoDate(value.from));
    setCustomTo(isoDate(value.to));
  }, [value.from, value.to]);

  // (Removed: tz-change recompute effect. The parent passes a stable tz
  // from `organization.timezone`, which doesn't change inside a session
  // in the current product. If we ever support per-session org switching
  // we'll bring this back with a useRef-based deps story that doesn't
  // trip CRA's eslint config.)

  const selectPreset = (key: RangePreset) => {
    localStorage.setItem(PRESET_KEY, key);
    if (key === 'custom') {
      onChange({ preset: 'custom', from: value.from, to: value.to });
      return;
    }
    const { from, to } = rangeForPreset(key, tz);
    onChange({ preset: key, from, to });
  };

  const applyCustom = () => {
    const from = parseLocalDateAtStart(customFrom);
    const to = parseLocalDateAtEnd(customTo);
    if (isNaN(from.getTime()) || isNaN(to.getTime()) || from >= to) {return;}
    localStorage.setItem(PRESET_KEY, 'custom');
    onChange({ preset: 'custom', from, to });
  };

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 8,
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 8,
      padding: 10,
    }}>
      {PRESET_BUTTONS.map(b => {
        const active = value.preset === b.key;
        return (
          <button
            key={b.key}
            onClick={() => selectPreset(b.key)}
            style={{
              padding: '6px 12px',
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? '#fff' : '#374151',
              background: active ? '#2563eb' : '#f3f4f6',
              border: '1px solid ' + (active ? '#2563eb' : '#e5e7eb'),
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            {b.label}
          </button>
        );
      })}
      {value.preset === 'custom' && (
        <>
          <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 4 }}>From</span>
          <input
            type="date"
            value={customFrom}
            onChange={e => setCustomFrom(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6 }}
          />
          <span style={{ color: '#6b7280', fontSize: 13 }}>To</span>
          <input
            type="date"
            value={customTo}
            onChange={e => setCustomTo(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6 }}
          />
          <button
            onClick={applyCustom}
            style={{
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: '#10b981',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Apply
          </button>
        </>
      )}
      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>
        Timezone: {tz}
      </span>
      {onRefresh && (
        <button
          onClick={onRefresh}
          title="Bypass cache and reload"
          style={{
            padding: '6px 12px',
            fontSize: 13,
            color: '#374151',
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          ↻ Refresh
        </button>
      )}
    </div>
  );
}
